/**
 * GameCard — extracted from GamesTab.jsx (Iter 141 refactor).
 *
 * Pure presentational tile rendered for each game in the lobby grid.
 * No business logic — all state owned by parent (GamesTab). This extract
 * cuts ~45 lines from the parent and gives a clear seam for any future
 * per-card UX (e.g. quick-play action button, balance flash on credit).
 *
 * Props:
 *   game           the game record (id, name, logo_image_url, hot, provider_status, …)
 *   favorites      array of favourite game ids
 *   onSelect(id)   open the game-detail popup
 *   onToggleFav(id) toggle favourite state
 */
import { memo } from 'react';
import { Play, Star } from 'lucide-react';
import { gameImageUrl } from '../../lib/utils';

const handleImageFallback = (event) => {
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === '1') return;
  img.dataset.fallbackApplied = '1';
  img.src = '/vegas-lobby-logo.webp';
};

function GameCard({ game, favorites, onSelect, onToggleFav }) {
  const gameImg = gameImageUrl(game);
  const isFav = favorites.includes(game.id);

  return (
    <div
      className="vl-game-card rounded-2xl overflow-hidden cursor-pointer anim-card-lift anim-press"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
      onClick={() => onSelect(game.id)}
      data-testid={`game-card-${game.id}`}
    >
      <div
        className="vl-game-card__media aspect-[4/3] rounded-t-2xl overflow-hidden relative"
        style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}
      >
        {gameImg ? (
          <img
            src={gameImg}
            alt={game.name}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={handleImageFallback}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-3xl font-bold bg-gradient-to-br from-[#1e1245] to-[#0d1117]"
            style={{ color: '#D4AF37' }}
          >
            {game.name?.charAt(0)}
          </div>
        )}
        <span className="vl-game-card__play" aria-hidden="true">
          <Play className="w-5 h-5 fill-current ml-0.5" />
        </span>
        {game.hot && (
          <span className="vl-game-card__hot absolute top-1.5 right-1.5" aria-label="Hot game">
            <img src="/assets/fire-svgrepo-com.svg" alt="" className="vl-game-card__fire-svg" draggable="false" />
          </span>
        )}
        {game.provider_status === 'manual_only' && (
          <div className="absolute top-1.5 left-1.5 text-[8px] px-1.5 py-0.5 rounded-full bg-orange-500/90 text-white font-bold">
            Maintenance
          </div>
        )}
        {game.upstream_status === 'broken' && (
          <div
            className="absolute top-1.5 left-1.5 text-[8px] px-1.5 py-0.5 rounded-full bg-red-600/90 text-white font-bold"
            data-testid={`game-card-upstream-broken-${game.id}`}
          >
            Temporarily Unavailable
          </div>
        )}
      </div>
      <div className="p-3.5">
        <div className="flex items-center justify-between">
          <h3 className="font-extrabold text-base text-white truncate">{game.name}</h3>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFav(game.id); }}
            className="p-0.5"
            data-testid={`fav-${game.id}`}
          >
            <Star
              className={`w-5 h-5 ${isFav ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-600'}`}
            />
          </button>
        </div>
        {game.provider_status === 'manual_only' && (
          <p className="text-xs text-orange-400 mt-0.5">Under Maintenance</p>
        )}
        {game.upstream_status === 'broken' && (
          <p
            className="text-xs text-red-400 mt-0.5 truncate"
            title={game.upstream_reason}
            data-testid={`game-card-upstream-reason-${game.id}`}
          >
            {game.upstream_reason || 'Temporarily unavailable'}
          </p>
        )}
      </div>
    </div>
  );
}

export default memo(GameCard);
